/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { VictoryPie, VictoryContainer, VictoryTooltip } from 'victory'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import type { AudienceBreakdownData } from './AudienceBreakdownSection'

const CHART_SIZE = 200

const CATEGORY_CONFIG: {
  key: keyof Omit<AudienceBreakdownData, 'total_users' | 'unique_users'>
  label: string
  color: string
}[] = [
  { key: 'admin_users', label: 'Admins', color: '#ffc726' },
  { key: 'league_admin_users', label: 'League Managers', color: '#f1d481' },
  { key: 'official_users', label: 'Officials', color: '#62b9ff' },
  { key: 'videographer_users', label: 'Videographers', color: '#bc37d3' },
  { key: 'player_users', label: 'Players', color: '#08e3ff' },
  { key: 'team_official_users', label: 'Team Officials', color: '#ffad33' },
  { key: 'following_users', label: 'Followers', color: '#6ec071' },
  { key: 'owner_users', label: 'Owners', color: '#e86826' },
]

const EMPTY_DATA = [{ x: ' ', y: 1 }]
const EMPTY_COLORS = [colors.NEUTRAL_600]

interface AudienceDonutChartProps {
  data: AudienceBreakdownData | null
  hasCalculated: boolean
}

const AudienceDonutChart: React.FC<AudienceDonutChartProps> = ({
  data,
  hasCalculated,
}) => {
  const activeCategories = data
    ? CATEGORY_CONFIG.filter((c) => data[c.key] > 0)
    : []
  const showEmpty = !hasCalculated || !data || activeCategories.length === 0

  const chartData = showEmpty
    ? EMPTY_DATA
    : activeCategories.map((c) => ({ x: c.label, y: data[c.key] }))

  const chartColors = showEmpty
    ? EMPTY_COLORS
    : activeCategories.map((c) => c.color)

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        opacity: ${hasCalculated && data ? 1 : 0.45};
        transition: opacity 0.4s ease;
      `}
    >
      {/* Chart with center label */}
      <div
        css={css`
          position: relative;
          width: ${CHART_SIZE}px;
          height: ${CHART_SIZE}px;
        `}
      >
        <VictoryPie
          data={chartData}
          colorScale={chartColors}
          width={CHART_SIZE}
          height={CHART_SIZE}
          innerRadius={70}
          padding={10}
          labels={
            showEmpty
              ? () => ''
              : ({ datum }) => `${datum.x}: ${datum.y.toLocaleString()}`
          }
          labelComponent={
            <VictoryTooltip
              flyoutStyle={{
                fill: colors.SECONDARY_300,
                stroke: colors.NEUTRAL_600,
                strokeWidth: 1,
              }}
              style={{
                fill: colors.WHITE,
                fontSize: 14,
              }}
              cornerRadius={4}
              flyoutPadding={{ top: 6, bottom: 6, left: 10, right: 10 }}
            />
          }
          containerComponent={
            <VictoryContainer
              responsive={false}
              width={CHART_SIZE}
              height={CHART_SIZE}
            />
          }
          animate={{ duration: 600, easing: 'cubicInOut' }}
        />
        {/* Center label overlay */}
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            pointer-events: none;
          `}
        >
          <Text variant='heading6' weight='bold' color={colors.WHITE}>
            {hasCalculated && data ? data.unique_users.toLocaleString() : '--'}
          </Text>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_100}
          >
            Unique Users
          </Text>
          {hasCalculated && data && (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {data.total_users.toLocaleString()}
            </Text>
          )}
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_100}
          >
            Total Users
          </Text>
        </div>
      </div>

      {/* Legend */}
      {!showEmpty && data && (
        <div
          css={css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px 24px;
            width: 100%;
          `}
        >
          {activeCategories.map((c) => (
            <div
              key={c.key}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <span
                css={css`
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                  background-color: ${c.color};
                  flex-shrink: 0;
                `}
              />
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                {c.label}: {data[c.key].toLocaleString()}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AudienceDonutChart
